import React, { useEffect, useState } from "react";
import { Link, RouteComponentProps } from "@reach/router";
import { Button } from "../components/Button";
import { TitleHeading } from "../components/Heading";
import { Wrapper } from "./Wrapper";
import { WelcomeCard } from "./JoinCard";
import { requiresAuth, useAuth } from "../context/AuthContext";
import styled from "styled-components";
import { CreateRoomDialog } from "../components/CreateRoomDialog";
import { Stack } from "../components/Stack";
import EnterRoomCode from "../components/EnterRoomCode";

type Props = RouteComponentProps;

const HeroSection = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const GameTitle = styled(TitleHeading)`
  font-size: 64px;
  background: linear-gradient(135deg, #fff 0%, rgba(255, 255, 255, 0.7) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 20px rgba(255, 255, 255, 0.15);
  margin: 0 0 16px 0;

  @media (max-width: 600px) {
    font-size: 48px;
  }
`;

const Subtitle = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  font-weight: 300;
`;

const Or = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 18px;
  position: relative;
  margin: 24px 0;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    width: 100px;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
  }

  &::before {
    right: calc(50% + 32px);
  }

  &::after {
    left: calc(50% + 32px);
  }
`;

const RoomsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px 0 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const RoomLink = styled(Link)`
  display: block;
  padding: 16px;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.9);
  background: linear-gradient(
    135deg,
    rgba(89, 44, 166, 0.3),
    rgba(66, 33, 123, 0.3)
  );
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(
      135deg,
      rgba(89, 44, 166, 0.4),
      rgba(66, 33, 123, 0.4)
    );
    box-shadow: 0 4px 20px -1px rgba(89, 44, 166, 0.2);
  }
`;

const Home: React.FC<Props> = () => {
  const { isAdmin } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const open = () => setDialogOpen(true);
  const close = () => setDialogOpen(false);
  const [rooms, setRooms] = useState<string[]>([]);

  useEffect(() => {
    if (isAdmin) {
      fetch("/api/__dump-rooms")
        .then((res) => res.json())
        .then((data) => {
          if (data && data.rooms) {
            setRooms(data.rooms);
          }
        });
    }
  }, [isAdmin]);

  return (
    <>
      <Wrapper>
        <HeroSection>
          <GameTitle>President.Game</GameTitle>
          <Subtitle>
            The classic card game, reimagined for the digital age
          </Subtitle>
        </HeroSection>

        <WelcomeCard>
          <Stack data-size="large">
            <EnterRoomCode />
            {isAdmin && (
              <>
                <Or>or create your own</Or>
                <Button onClick={open} isFancy>
                  Create New Room
                </Button>
                {rooms.length > 0 && (
                  <>
                    <Or>Active Rooms</Or>
                    <RoomsList>
                      {rooms.map((r) => (
                        <li key={r}>
                          <RoomLink to={"/" + r}>{r}</RoomLink>
                        </li>
                      ))}
                    </RoomsList>
                  </>
                )}
              </>
            )}
          </Stack>
        </WelcomeCard>
        <CreateRoomDialog isOpen={dialogOpen} onDismiss={close} />
      </Wrapper>
    </>
  );
};

export default requiresAuth(Home);
